import React, { useMemo, useState } from 'react';
import type { SiteEvent, SeverityLevel } from '../../../redux/api/eventsApi';
import { SEVERITY_HEX_COLORS } from '../../../constants/eventStyles';
import type { UpcomingPortfolioEvent } from '../../../mocks/upcomingEventsMock';
import { UPCOMING_EVENT_TYPE_CONFIG } from '../../../mocks/upcomingEventsMock';

interface TopEventsModuleProps {
  events: SiteEvent[];
  upcomingEvents?: UpcomingPortfolioEvent[];
  onEventClick?: (eventId: string) => void;
  onUpcomingClick?: (event: UpcomingPortfolioEvent) => void;
  showSiteName?: boolean;
  maxItems?: number;
}

type SortMode = 'latest' | 'critical' | 'safety' | 'upcoming';

const SEVERITY_WEIGHT: Record<SeverityLevel, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const SORT_LABELS: Record<SortMode, string> = {
  latest: 'Recent',
  critical: 'Critical',
  safety: 'Safety',
  upcoming: 'Upcoming',
};

function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  if (diffMs < 0) {
    // Future event
    const absDiff = Math.abs(diffMs);
    const hours = Math.floor(absDiff / (1000 * 60 * 60));
    if (hours < 24) return `in ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `in ${days}d`;
    if (days < 30) return `in ${Math.floor(days / 7)}w`;
    if (days < 365) return `in ${Math.floor(days / 30)}mo`;
    return `in ${(days / 365).toFixed(1)}yr`;
  }
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function urgencyColor(dateString: string): string {
  const days = Math.ceil(
    (new Date(dateString).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  if (days < 0) return '#EF4444';
  if (days <= 7) return '#EF4444';
  if (days <= 30) return '#F97316';
  if (days <= 90) return '#F59E0B';
  if (days <= 365) return '#3B82F6';
  return '#6B7280';
}

const TopEventsModule: React.FC<TopEventsModuleProps> = ({
  events,
  upcomingEvents = [],
  onEventClick,
  onUpcomingClick,
  showSiteName = false,
  maxItems = 5,
}) => {
  const [sortMode, setSortMode] = useState<SortMode>('latest');

  const sortedEvents = useMemo(() => {
    if (sortMode === 'upcoming') return []; // handled separately

    const sorted = [...events];
    switch (sortMode) {
      case 'latest':
        sorted.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
        break;
      case 'critical':
        sorted.sort((a, b) => {
          // critical/high first, then by recency
          const wA = SEVERITY_WEIGHT[a.severity];
          const wB = SEVERITY_WEIGHT[b.severity];
          if (wA !== wB) return wB - wA;
          return new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
        });
        // Only show critical+high
        return sorted.filter((e) => e.severity === 'critical' || e.severity === 'high').slice(0, maxItems);
      case 'safety':
        // Safety = incidents + open critical
        return sorted
          .filter((e) => {
            const isSafety = e.event_type === 'incident';
            const isOpenCritical = e.status === 'open' && e.severity === 'critical';
            return isSafety || isOpenCritical;
          })
          .sort((a, b) => {
            const wA = SEVERITY_WEIGHT[a.severity];
            const wB = SEVERITY_WEIGHT[b.severity];
            if (wA !== wB) return wB - wA;
            return new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
          })
          .slice(0, maxItems);
    }
    return sorted.slice(0, maxItems);
  }, [events, sortMode, maxItems]);

  // Upcoming events sorted by nearest due date
  const sortedUpcoming = useMemo(() => {
    if (sortMode !== 'upcoming') return [];
    return [...upcomingEvents]
      .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
      .slice(0, maxItems);
  }, [upcomingEvents, sortMode, maxItems]);

  return (
    <div className="space-y-3">
      {/* Sort Toggle */}
      <div className="flex gap-1 flex-wrap">
        {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => setSortMode(mode)}
            className="px-2.5 py-1 rounded text-xs font-medium transition-all"
            style={{
              backgroundColor: sortMode === mode ? 'rgba(23, 196, 128, 0.15)' : 'transparent',
              color: sortMode === mode ? '#17c480' : 'var(--color-text-muted)',
            }}
          >
            {SORT_LABELS[mode]}
          </button>
        ))}
      </div>

      {/* Events List (for non-upcoming modes) */}
      {sortMode !== 'upcoming' && (
        <>
          {sortedEvents.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-muted)' }}>
              No {sortMode === 'safety' ? 'safety' : sortMode === 'critical' ? 'critical' : ''} events
            </p>
          ) : (
            <div className="space-y-1">
              {sortedEvents.map((event) => (
                <button
                  key={event.id}
                  onClick={() => onEventClick?.(event.id)}
                  className="w-full text-left p-2.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)]"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                      style={{ backgroundColor: SEVERITY_HEX_COLORS[event.severity] }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                        {event.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {showSiteName && (
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            {event.site_id}
                          </span>
                        )}
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          {formatTimeAgo(event.occurred_at)}
                        </span>
                        {event.energy_impact_kwh != null && event.energy_impact_kwh > 0 && (
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            Est. {(event.energy_impact_kwh / 1000).toFixed(1)} MWh lost
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Upcoming Events List */}
      {sortMode === 'upcoming' && (
        <>
          {sortedUpcoming.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-muted)' }}>
              No upcoming events
            </p>
          ) : (
            <div className="space-y-1">
              {sortedUpcoming.map((event) => {
                const config = UPCOMING_EVENT_TYPE_CONFIG[event.event_type];
                return (
                  <button
                    key={event.id}
                    onClick={() => onUpcomingClick?.(event)}
                    className="w-full text-left p-2.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)]"
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: config.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                          {event.title}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            {event.site_name}
                          </span>
                          <span
                            className="text-xs font-medium"
                            style={{ color: urgencyColor(event.due_date) }}
                          >
                            {formatTimeAgo(event.due_date)}
                          </span>
                          <span
                            className="text-[10px] px-1 py-0.5 rounded"
                            style={{
                              backgroundColor: `${config.color}15`,
                              color: config.color,
                            }}
                          >
                            {config.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopEventsModule;
